import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, LogIn } from "lucide-react";
import Header from "@/components/Header";
import { AuthModal } from "@/components/AuthModal";

const TABLES = [
  { key: "user_enrollments", label: "Academy Enrollments" },
  { key: "certificates", label: "Certificates" },
  { key: "lesson_progress", label: "Lesson Progress" },
  { key: "contact_submissions", label: "Contact Forms" },
  { key: "demo_requests", label: "Demo Requests" },
  { key: "team_training_leads", label: "Team Training Leads" },
  { key: "assessments", label: "Digital Assessments" },
  { key: "rpa_assessments", label: "RPA Assessments" },
  { key: "training_bookings", label: "Training Bookings" },
  { key: "training_needs_assessments", label: "Training Needs" },
  { key: "zone_analysis_requests", label: "Zone Analysis Requests" },
] as const;

export default function Admin() {
  const [session, setSession] = useState<any>(null);
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [data, setData] = useState<Record<string, any[]>>({});
  const [loading, setLoading] = useState(false);

  const check = async (sess?: any) => {
    try {
      let s = sess;
      if (s === undefined) {
        const { data } = await supabase.auth.getSession();
        s = data.session;
      }
      setSession(s);
      if (!s) { setIsAdmin(false); return; }
      const { data: roles, error } = await supabase
        .from("user_roles" as any)
        .select("role")
        .eq("user_id", s.user.id)
        .eq("role", "admin");
      if (error) console.error("[Admin] roles error", error);
      const admin = !!roles && roles.length > 0;
      setIsAdmin(admin);
      if (admin) loadAll();
    } catch (e) {
      console.error("[Admin] check failed", e);
      setIsAdmin(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    check();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      // Avoid flashing the full-page spinner on token refreshes
      check(s);
    });
    return () => sub.subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const results: Record<string, any[]> = {};
    await Promise.all(
      TABLES.map(async (t) => {
        if (t.key === "user_enrollments") {
          const { data, error } = await supabase.rpc("admin_list_enrollments" as any);
          results[t.key] = error ? [] : (data as any[]) || [];
          return;
        }
        if (t.key === "certificates") {
          const { data, error } = await supabase.rpc("admin_list_certificates" as any);
          results[t.key] = error ? [] : (data as any[]) || [];
          return;
        }
        const orderMap: Record<string, string> = {
          certificates: "issued_at",
          lesson_progress: "completed_at",
        };
        const orderCol = orderMap[t.key] ?? "created_at";
        const { data, error } = await supabase
          .from(t.key as any)
          .select("*")
          .order(orderCol, { ascending: false })
          .limit(500);
        results[t.key] = error ? [] : (data as any[]) || [];
      })
    );
    setData(results);
    setLoading(false);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 text-center">
        <Card className="p-8 max-w-md space-y-4">
          <ShieldCheck className="h-10 w-10 text-primary mx-auto" />
          <h1 className="text-2xl font-bold">Admin access required</h1>
          {!session ? (
            <>
              <p className="text-muted-foreground">Please sign in with the admin account to continue.</p>
              <Button onClick={() => setAuthOpen(true)} className="w-full">
                <LogIn className="h-4 w-4 mr-2" /> Sign in
              </Button>
            </>
          ) : (
            <p className="text-muted-foreground">
              Signed in as <strong>{session.user.email}</strong>, but this account does not have admin privileges.
            </p>
          )}
        </Card>
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} onSuccess={() => { setAuthOpen(false); check(); }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground text-sm">All submissions and academy activity</p>
          </div>
        </div>

        <AnalyticsOverview data={data} />

        <Tabs defaultValue={TABLES[0].key}>
          <TabsList className="flex flex-wrap h-auto">
            {TABLES.map((t) => (
              <TabsTrigger key={t.key} value={t.key} className="text-xs">
                {t.label}
                <Badge variant="secondary" className="ml-2">{data[t.key]?.length ?? 0}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>

          {TABLES.map((t) => (
            <TabsContent key={t.key} value={t.key}>
              <Card className="p-0 overflow-hidden">
                <div
                  className="overflow-auto max-h-[70vh] [&::-webkit-scrollbar]:h-3 [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-thumb]:bg-primary/60 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-track]:bg-muted"
                  style={{ scrollbarColor: "hsl(var(--primary)) hsl(var(--muted))", scrollbarWidth: "thin" }}
                >
                  <div className="p-4">
                    {loading ? (
                      <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin h-4 w-4" /> Loading…</div>
                    ) : (data[t.key]?.length ?? 0) === 0 ? (
                      <p className="text-muted-foreground text-sm">No records.</p>
                    ) : (
                      <DataTable rows={data[t.key]} />
                    )}
                  </div>
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}

function DataTable({ rows }: { rows: any[] }) {
  const cols = Array.from(
    rows.reduce<Set<string>>((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set())
  );
  return (
    <table className="text-xs border-collapse" style={{ minWidth: "100%" }}>
      <thead className="bg-muted sticky top-0 z-10">
        <tr>{cols.map((c) => <th key={c} className="text-left p-2 border-b font-medium whitespace-nowrap">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.id ?? i} className="hover:bg-muted/50 border-b">
            {cols.map((c) => (
              <td key={c} className="p-2 align-top max-w-[260px] truncate whitespace-nowrap" title={fmt(r[c])}>
                {fmt(r[c])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function fmt(v: any): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function AnalyticsOverview({ data }: { data: Record<string, any[]> }) {
  const enrollments = data.user_enrollments ?? [];
  const certificates = data.certificates ?? [];
  const lessonProgress = data.lesson_progress ?? [];

  const totalEnrollments = enrollments.length;
  const completedEnrollments = enrollments.filter((e: any) => e.completed_at || e.progress_percentage === 100).length;
  const completionRate = totalEnrollments ? Math.round((completedEnrollments / totalEnrollments) * 100) : 0;
  const totalCertificates = certificates.length;
  const lessonsCompleted = lessonProgress.filter((p: any) => p.completed).length;

  const items = [
    { label: "Total Enrollments", value: totalEnrollments },
    { label: "Completed Courses", value: completedEnrollments },
    { label: "Completion Rate", value: `${completionRate}%` },
    { label: "Certificates Issued", value: totalCertificates },
    { label: "Lessons Completed", value: lessonsCompleted },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
      {items.map((i) => (
        <Card key={i.label} className="p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{i.label}</div>
          <div className="text-2xl font-bold text-primary mt-1">{i.value}</div>
        </Card>
      ))}
    </div>
  );
}
